import React, { useState, useEffect, useRef } from 'react';
import { Search, Send, Check, CheckCheck, ThumbsUp, ThumbsDown, Info, MoreVertical, X } from 'lucide-react';
import Navigation from '../../components/Navigation';
import { messageService } from '../../services/messageService';
import { useAuth } from '../../contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import { sr } from 'date-fns/locale';
import realtimeService from '../../services/realtimeService';
import { statusGuardService } from '../../services/statusGuardService';

const Messages = () => {
  const { user } = useAuth();
  const [negotiations, setNegotiations] = useState([]);
  const [selectedNegotiation, setSelectedNegotiation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  useEffect(() => {
    if (user?.id) {
      loadNegotiations();
    }
  }, [user?.id]);

  useEffect(() => {
    if (selectedNegotiation) {
      loadMessages(selectedNegotiation?.id);
      
      // Mark messages as read
      messageService?.markMessagesAsRead(selectedNegotiation?.id, user?.id);

      // Subscribe to real-time message updates using enhanced realtimeService
      const messageChannel = realtimeService?.subscribeToNegotiationMessages(
        selectedNegotiation?.id,
        {
          onInsert: (newMessage) => {
            console.log('Real-time: New message received', newMessage);
            loadMessages(selectedNegotiation?.id);
          },
          onUpdate: (updatedMessage) => {
            console.log('Real-time: Message updated', updatedMessage);
            loadMessages(selectedNegotiation?.id);
          }
        }
      );

      // Subscribe to typing indicators
      const typingChannel = messageService?.subscribeToTypingIndicators(
        selectedNegotiation?.id,
        (typingStatus) => {
          setTypingUsers(typingStatus || []);
        }
      );

      // Load initial typing status
      loadTypingStatus(selectedNegotiation?.id);

      return () => {
        if (messageChannel) {
          realtimeService?.unsubscribe(messageChannel);
        }
        if (typingChannel) {
          messageService?.unsubscribeFromTypingIndicators(typingChannel);
        }
      };
    }
  }, [selectedNegotiation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, typingUsers]);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadTypingStatus = async (negotiationId) => {
    try {
      const status = await messageService?.getTypingStatus(negotiationId);
      setTypingUsers(status || []);
    } catch (error) {
      console.error('Error loading typing status:', error);
    }
  };

  const loadNegotiations = async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      const data = await messageService?.getUserNegotiations(user?.id);
      setNegotiations(data || []);
      
      // Auto-select first negotiation if available
      if (data && data?.length > 0 && !selectedNegotiation) {
        setSelectedNegotiation(data?.[0]);
      }
    } catch (error) {
      console.error('Error loading negotiations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (negotiationId) => {
    try {
      const data = await messageService?.getNegotiationMessages(negotiationId);
      setMessages(data || []);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const handleSendMessage = async (messageText = null, messageType = 'text') => {
    const textToSend = messageText || newMessage?.trim();
    if (!textToSend || !selectedNegotiation) return;

    // Status confirmation guard
    const guardResult = await statusGuardService?.checkStatusConfirmation();
    if (!guardResult?.hasAccess) {
      alert(guardResult?.message || 'Morate potvrditi status da biste mogli da šaljete poruke');
      return;
    }

    setSending(true);
    try {
      await messageService?.sendMessage(
        selectedNegotiation?.id,
        textToSend,
        messageType
      );
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Greška pri slanju poruke. Pokušajte ponovo.');
    } finally {
      setSending(false);
    }
  };

  const handleQuickAction = (action) => {
    handleSendMessage(action?.text, action?.type);
  };

  const handleSearch = async () => {
    if (!searchTerm?.trim() || !user?.id) return;

    setLoading(true);
    try {
      const results = await messageService?.searchMessages(user?.id, searchTerm);
      setSearchResults(results || []);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Error searching messages:', error);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = async (e) => {
    setNewMessage(e?.target?.value);

    // Clear existing typing timeout
    if (typingTimeoutRef?.current) {
      clearTimeout(typingTimeoutRef?.current);
    }

    // Set typing status to true
    if (selectedNegotiation && e?.target?.value?.trim()) {
      await messageService?.setTypingStatus(selectedNegotiation?.id, true);

      // Set timeout to clear typing status after 3 seconds of inactivity
      typingTimeoutRef.current = setTimeout(async () => {
        await messageService?.setTypingStatus(selectedNegotiation?.id, false);
      }, 3000);
    } else if (selectedNegotiation) {
      await messageService?.setTypingStatus(selectedNegotiation?.id, false);
    }
  };

  const closeSearchResults = () => {
    setShowSearchResults(false);
    setSearchResults([]);
    setSearchTerm('');
  };

  const selectSearchResult = (message) => {
    // Find and select the negotiation from search result
    const negotiation = negotiations?.find(n => n?.id === message?.negotiationId);
    if (negotiation) {
      setSelectedNegotiation(negotiation);
      closeSearchResults();
    }
  };

  const getOtherParticipant = (negotiation) => {
    if (!negotiation || !user?.id) return null;
    
    return negotiation?.initiatorId === user?.id 
      ? negotiation?.responder 
      : negotiation?.initiator;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Poruke</h1>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 h-[calc(100vh-200px)]">
          {/* Conversations List */}
          <div className="md:col-span-4 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden flex flex-col">
            {/* Search Header */}
            <div className="p-4 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Pretraži poruke..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  onKeyPress={(e) => e?.key === 'Enter' && handleSearch()}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-700 focus:border-transparent"
                />
              </div>
            </div>

            {/* Conversations */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700"></div>
                </div>
              ) : negotiations?.length === 0 ? (
                <div className="text-center py-12 px-4">
                  <p className="text-gray-500">Nemate aktivnih pregovora</p>
                </div>
              ) : (
                negotiations?.map((negotiation) => {
                  const otherParticipant = getOtherParticipant(negotiation);
                  return (
                    <button
                      key={negotiation?.id}
                      onClick={() => setSelectedNegotiation(negotiation)}
                      className={`w-full p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors text-left ${
                        selectedNegotiation?.id === negotiation?.id ? 'bg-blue-50' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 bg-brand-primary rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                          {otherParticipant?.fullName?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-900 truncate">
                            {otherParticipant?.fullName || 'Korisnik'}
                          </p>
                          <p className="text-sm text-gray-600 truncate">
                            {negotiation?.wish?.voucher?.title || 'Pregovor'}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {formatDistanceToNow(new Date(negotiation.updatedAt), {
                              addSuffix: true,
                              locale: sr
                            })}
                          </p>
                        </div>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          negotiation?.status === 'initiated' ? 'bg-blue-100 text-blue-700' :
                          negotiation?.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
                          negotiation?.status === 'agreed'? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {negotiation?.status}
                        </span>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* Chat Area */}
          <div className="md:col-span-8 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden flex flex-col">
            {selectedNegotiation ? (
              <>
                {/* Chat Header */}
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-brand-primary rounded-full flex items-center justify-center text-white font-bold">
                      {getOtherParticipant(selectedNegotiation)?.fullName?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">
                        {getOtherParticipant(selectedNegotiation)?.fullName || 'Korisnik'}
                      </p>
                      <p className="text-sm text-gray-600">
                        {selectedNegotiation?.wish?.voucher?.title || 'Pregovor'}
                      </p>
                      {typingUsers?.length > 0 && (
                        <p className="text-xs text-teal-700 italic">
                          {typingUsers?.[0]?.userName} kuca...
                        </p>
                      )}
                    </div>
                  </div>
                  <button className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                    <MoreVertical size={20} />
                  </button>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {messages?.map((message) => {
                    const isOwnMessage = message?.senderId === user?.id;
                    return (
                      <div
                        key={message?.id}
                        className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[70%] rounded-lg px-4 py-2 ${
                            isOwnMessage
                              ? 'bg-brand-primary text-white' :'bg-gray-100 text-gray-900'
                          }`}
                        >
                          <p className="text-sm break-words whitespace-pre-wrap">
                            {message?.messageContent}
                          </p>
                          <div className={`flex items-center gap-1 mt-1 text-xs ${
                            isOwnMessage ? 'text-teal-100' : 'text-gray-500'
                          }`}>
                            <span>
                              {formatDistanceToNow(new Date(message.createdAt), {
                                addSuffix: true,
                                locale: sr
                              })}
                            </span>
                            {isOwnMessage && (
                              <span className="flex items-center gap-0.5">
                                {message?.isRead ? (
                                  <>
                                    <CheckCheck size={14} />
                                    {message?.readAt && (
                                      <span className="text-xs ml-1">
                                        {formatDistanceToNow(new Date(message.readAt), {
                                          addSuffix: true,
                                          locale: sr
                                        })}
                                      </span>
                                    )}
                                  </>
                                ) : (
                                  <Check size={14} />
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {typingUsers?.length > 0 && (
                    <div className="flex justify-start">
                      <div className="bg-gray-100 rounded-lg px-4 py-2">
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Quick Actions */}
                <div className="px-4 py-2 border-t border-gray-100 flex gap-2 overflow-x-auto">
                  <button
                    onClick={() => handleQuickAction(messageService?.quickActions?.accept)}
                    className="flex items-center gap-1 px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-sm font-medium hover:bg-green-200 transition-colors whitespace-nowrap"
                  >
                    <ThumbsUp size={14} />
                    Prihvatam
                  </button>
                  <button
                    onClick={() => handleQuickAction(messageService?.quickActions?.reject)}
                    className="flex items-center gap-1 px-3 py-1.5 bg-red-100 text-red-700 rounded-full text-sm font-medium hover:bg-red-200 transition-colors whitespace-nowrap"
                  >
                    <ThumbsDown size={14} />
                    Odbijam
                  </button>
                  <button
                    onClick={() => handleQuickAction(messageService?.quickActions?.counter)}
                    className="flex items-center gap-1 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm font-medium hover:bg-blue-200 transition-colors whitespace-nowrap"
                  >
                    <Info size={14} />
                    Protuponuda
                  </button>
                  <button
                    onClick={() => handleQuickAction(messageService?.quickActions?.requestInfo)}
                    className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full text-sm font-medium hover:bg-gray-200 transition-colors whitespace-nowrap"
                  >
                    <Info size={14} />
                    Dodatne info
                  </button>
                </div>

                {/* Message Input */}
                <div className="p-4 border-t border-gray-200">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Napišite poruku..."
                      value={newMessage}
                      onChange={handleInputChange}
                      onKeyPress={(e) => e?.key === 'Enter' && !e?.shiftKey && handleSendMessage()}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-700 focus:border-transparent"
                      disabled={sending}
                    />
                    <button
                      onClick={() => handleSendMessage()}
                      disabled={sending || !newMessage?.trim()}
                      className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      <Send size={20} />
                      Pošalji
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-500">
                <p>Izaberite pregovor da biste videli poruke</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Search Results Modal */}
      {showSearchResults && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Rezultati pretrage: {searchResults?.length} poruka
              </h3>
              <button
                onClick={closeSearchResults}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {searchResults?.length === 0 ? (
                <p className="text-center text-gray-500 py-8">
                  Nema rezultata za "{searchTerm}"
                </p>
              ) : (
                searchResults?.map((result) => (
                  <button
                    key={result?.id}
                    onClick={() => selectSearchResult(result)}
                    className="w-full text-left p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-brand-primary rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                        {result?.sender?.fullName?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-semibold text-gray-900">
                            {result?.sender?.fullName || 'Korisnik'}
                          </p>
                          <span className="text-xs text-gray-400">
                            {formatDistanceToNow(new Date(result.createdAt), {
                              addSuffix: true,
                              locale: sr
                            })}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-1">
                          {result?.negotiation?.voucherTitle}
                        </p>
                        <p className="text-sm text-gray-700 line-clamp-2">
                          {result?.messageContent}
                        </p>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Messages;